import React, { useState, useEffect } from 'react';
import { Card, Button, Typography, Divider, message, Skeleton, Badge, Tag, Modal } from 'antd';
import { 
  CrownOutlined, 
  UserOutlined, 
  MailOutlined, 
  CalendarOutlined, 
  ArrowLeftOutlined,
  LogoutOutlined 
} from '@ant-design/icons';
import { auth, db } from '../firebase-config';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { httpsCallable, getFunctions } from 'firebase/functions';
import { useDispatch } from 'react-redux';
import { changeSubscription } from '../Store/allreducer';

const { Title, Text } = Typography;

const SettingsScreen = () => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const functions = getFunctions()
  const cancelSubscription = httpsCallable(functions, 'cancelSubscription');

  useEffect(() => {
    const handlePaymentRedirect = async () => {
      const params = new URLSearchParams(location.search);
      const subscription_id = params.get('subscription_id');
      const status = params.get('status');

      if (subscription_id && status === 'active') {
        try {
          const user = auth.currentUser;
          if (!user) {
            message.error('Please sign in first');
            return;
          }

          const subscriptionData = {
            subscription: true,
            subscriptionStartDate: new Date().toISOString(),
            subscription_id: subscription_id,
            lastUpdated: new Date().toISOString()
          };

          await updateDoc(doc(db, 'users', user.uid), subscriptionData);
          dispatch(changeSubscription(true));
          await fetchUserData();
          message.success('Subscription activated successfully! 🎉');

          // Clean up URL parameters (optional)
          navigate('/settings', { replace: true });
        } catch (error) {
          console.error('Error updating subscription:', error);
          message.error('Failed to update subscription status');
        }
      }
    };

    fetchUserData();
    handlePaymentRedirect();
  }, [location, dispatch, navigate]);

  const fetchUserData = async () => {
    try {
      const user = auth.currentUser;
      if (user) {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          setUserData(userDoc.data());
        }
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching user data:', error);
      message.error('Failed to load user data');
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        message.error('Please sign in first');
        return;
      }

      let checkoutUrl;
       if(userData?.email === 'shubhankardev8@gmail.com'){
       checkoutUrl = `https://checkout.dodopayments.com/buy/pdt_YMl0F1TjxWaEWU2sXUpDv?quantity=1&redirect_url=https://carouselpost.in%2Fsettings`;
       }else{
        checkoutUrl = `https://checkout.dodopayments.com/buy/pdt_vg9156QH3SiO2HS61pqTN?quantity=1&redirect_url=https://carouselpost.in%2Fsettings`;
       }
      window.location.href = checkoutUrl;
    } catch (error) {
      console.error('Error:', error);
      message.error('Failed to initiate subscription');
    }
  };

  const handleCancelSubscription = async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        message.error('Please sign in first');
        return;
      }

      const subscriptionData = {
        subscription: false,
        subscriptionStartDate: null,
      };
      // get subscription id from user data
      const subscriptionId = userData?.subscription_id;
      const result = await cancelSubscription({ subscriptionId: subscriptionId });
      console.log('Subscription cancelled:', result.data);
      await updateDoc(doc(db, 'users', user.uid), subscriptionData);
      dispatch(changeSubscription(false));
      await fetchUserData();
      message.success('Subscription cancelled successfully');
    } catch (error) {
      console.error('Error:', error);
      message.error('Failed to cancel subscription');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      message.success('Logged out successfully');
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      message.error('Failed to logout');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen p-8 bg-gradient-to-br from-purple-50 to-pink-50">
        <div className="max-w-3xl mx-auto">
          <Skeleton active paragraph={{ rows: 6 }} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-8 bg-gradient-to-br from-purple-50 to-pink-50">
      {/* Back Button */}
      <div className="absolute top-4 left-4">
        <Link to="/edit">
          <Button 
            type="text"
            icon={<ArrowLeftOutlined />}
            className="flex items-center text-purple-700 hover:text-purple-900 font-semibold text-lg hover:bg-purple-50 rounded-full px-6 py-2 h-auto"
          >
            Back to Editor
          </Button>
        </Link>
      </div>

      <div className="max-w-3xl mx-auto">
        <Title level={4} className="text-center mb-8 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent text-xl md:text-3xl lg:text-4xl">
          Account Settings ⚙️
        </Title>

        {/* Modified User Profile Card with Logout */}
        <Card className="mb-8 shadow-md rounded-2xl">
          <div className="flex items-center mb-4">
            <div className="w-16 h-16 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full flex items-center justify-center">
              <UserOutlined className="text-white text-2xl" />
            </div>
            <div className="ml-4 flex-1">
              <div className="flex justify-between items-start">
                <div>
                  <Title level={3} className="mb-0">{userData?.username || auth.currentUser?.displayName}</Title>
                  <Text type="secondary"><MailOutlined className="mr-2" />{userData?.email}</Text>
                </div>
                <Button 
                  icon={<LogoutOutlined />}
                  onClick={handleLogout}
                  danger
                  ghost
                  className="hover:bg-red-50"
                >
                  Logout
                </Button>
              </div>
            </div>
          </div>
          <Divider />
          <div className="space-y-2">
            <Text><CalendarOutlined className="mr-2" />Joined: {formatDate(userData?.createdAt)}</Text>
            <div>
              <Text className="mr-2">Status:</Text>
              {userData?.subscription ? (
                <Tag color="success" icon={<CrownOutlined />}>Premium Member</Tag>
              ) : (
                <Tag color="default">Free Plan</Tag>
              )}
            </div>
          </div>
        </Card>

        {/* Features Card */}
        <Card className="shadow-md rounded-2xl">
          <Title level={3} className="flex items-center">
            <CrownOutlined className="mr-2 text-yellow-500" />
            Premium Features
          </Title>
          
          {userData?.subscription ? (
            <div className="space-y-4">
              <Badge status="success" text="Premium Features Activated" />
              <div>
                <Text type="secondary">Activated on: {formatDate(userData?.subscriptionStartDate)}</Text>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <Text className="text-green-800">
                  You have access to all premium features! 🎉
                </Text>
                <ul className="list-disc list-inside space-y-2 mt-4 text-green-800">
                  <li>Unlimited Downloads</li>
                  <li>Carousel Generator</li>
                  <li>Access to premium templates and design elements</li>
                  <li>Advanced customization options</li>
                  <li>Advanced Image support</li>
                  <li>Priority support</li>
                </ul>
              </div>
              <Divider />
              <div className="text-center">
                <Button 
                  danger
                  type="text"
                  onClick={()=> Modal.confirm({
                    title: 'Cancel Subscription',
                    content: 'Are you sure you want to cancel your subscription?',
                    onOk: handleCancelSubscription,
                    okText: 'Yes',
                    cancelText: 'No',
                  })}
                  className="hover:bg-red-50"
                >
                  Cancel Subscription
                </Button>
                <div className="mt-2">
                  <Text type="secondary" className="text-xs">
                    Cancelling will remove access to premium features
                  </Text>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="relative overflow-hidden bg-white rounded-2xl shadow-xl border border-purple-100">
                {/* Popular Badge */}
                <div className="absolute top-5 right-5">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                    Most Popular ⭐
                  </span>
                </div>

                {/* Card Content */}
                <div className="p-8">
                  {/* Header */}
                  <div className="text-center mb-8">
                    <h3 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-2">
                      Pro Plan
                    </h3>
                    <div className="flex items-baseline justify-center gap-2">
                      <span className="text-4xl font-bold">$10</span>
                      <span className="text-gray-500">/month</span>
                    </div>
                  </div>

                  {/* Divider with Gradient */}
                  <div className="relative my-8">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-200"></div>
                    </div>
                    <div className="relative flex justify-center">
                      <span className="bg-white px-4 text-sm text-gray-500">Premium Features</span>
                    </div>
                  </div>

                  {/* Features List */}
                  <ul className="space-y-4 mb-8">
                    <li className="flex items-center gap-3">
                      <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full bg-purple-100 text-purple-600">✓</span>
                      <span className="text-gray-700">Unlimited Downloads</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full bg-purple-100 text-purple-600">✓</span>
                      <span className="text-gray-700">Carousel Generator</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full bg-purple-100 text-purple-600">✓</span>
                      <span className="text-gray-700">Premium Templates & Design Elements</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full bg-purple-100 text-purple-600">✓</span>
                      <span className="text-gray-700">Advanced Customization Options</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full bg-purple-100 text-purple-600">✓</span>
                      <span className="text-gray-700">Advanced Image Support</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full bg-purple-100 text-purple-600">✓</span>
                      <span className="text-gray-700">Priority Support</span>
                    </li>
                  </ul>

                  {/* CTA Button */}
                  <Button
                    type="primary"
                    size="large"
                    icon={<CrownOutlined />}
                    onClick={handleSubscribe}
                    className="w-full h-14 text-lg bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 border-0 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    Upgrade to Pro 🚀
                  </Button>

                  {/* Money Back Guarantee */}
                  <p className="text-center text-sm text-gray-500 mt-4">
                    30-day money-back guarantee
                  </p>
                </div>

                {/* Decorative Corner Elements */}
                <div className="absolute top-0 left-0 w-20 h-20 bg-gradient-to-br from-purple-100 to-transparent opacity-50 rounded-br-full"></div>
                <div className="absolute bottom-0 right-0 w-20 h-20 bg-gradient-to-tl from-pink-100 to-transparent opacity-50 rounded-tl-full"></div>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default SettingsScreen;
